import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { CustomCursor } from "@/components/ui/CustomCursor";
import Index from "./pages/Index";

const Auth = lazy(() => import("./pages/Auth"));
const ChiSono = lazy(() => import("./pages/ChiSono"));
const Massaggi = lazy(() => import("./pages/Massaggi"));
const Tantra = lazy(() => import("./pages/Tantra"));
const ServiceDetail = lazy(() => import("./pages/ServiceDetail"));
const Corsi = lazy(() => import("./pages/Corsi"));
const Eventi = lazy(() => import("./pages/Eventi"));
const EventDetail = lazy(() => import("./pages/EventDetail"));
const CalendarioEventi = lazy(() => import("./pages/CalendarioEventi"));
const GiftCard = lazy(() => import("./pages/GiftCard"));
const Contatti = lazy(() => import("./pages/Contatti"));
const Booking = lazy(() => import("./pages/Booking"));
const Checkout = lazy(() => import("./pages/Checkout"));
const OrderConfirmation = lazy(() => import("./pages/OrderConfirmation"));
const AreaPersonale = lazy(() => import("./pages/AreaPersonale"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TerminiCondizioni = lazy(() => import("./pages/TerminiCondizioni"));
const Recesso = lazy(() => import("./pages/Recesso"));
const AdminRecessi = lazy(() => import("./pages/admin/AdminRecessi"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminHome = lazy(() => import("./pages/admin/AdminHome"));
const AdminOrders = lazy(() => import("./pages/admin/AdminOrders"));
const AdminBookings = lazy(() => import("./pages/admin/AdminBookings"));
const AdminBookingAudit = lazy(() => import("./pages/admin/AdminBookingAudit"));
const AdminReviews = lazy(() => import("./pages/admin/AdminReviews"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminHours = lazy(() => import("./pages/admin/AdminHours"));
const AdminPages = lazy(() => import("./pages/admin/AdminPages"));
const AdminServices = lazy(() => import("./pages/admin/AdminServices"));
const AdminEvents = lazy(() => import("./pages/admin/AdminEvents"));
const AdminCourses = lazy(() => import("./pages/admin/AdminCourses"));


const AdminGiftCards = lazy(() => import("./pages/admin/AdminGiftCards"));
const AdminEmails = lazy(() => import("./pages/admin/AdminEmails"));
const AdminPayments = lazy(() => import("./pages/admin/AdminPayments"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const PageFallback = () => (
  <div className="min-h-screen bg-background" aria-hidden="true" />
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <CustomCursor />
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<PageFallback />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/chi-sono" element={<ChiSono />} />
              <Route path="/massaggi" element={<Massaggi />} />
              <Route path="/tantra" element={<Tantra />} />
              <Route path="/servizi/:slug" element={<ServiceDetail />} />
              <Route path="/corsi" element={<Corsi />} />
              <Route path="/eventi" element={<Eventi />} />
              <Route path="/eventi/:slug" element={<EventDetail />} />
              <Route path="/calendario-eventi" element={<CalendarioEventi />} />
              <Route path="/gift-card" element={<GiftCard />} />
              <Route path="/contatti" element={<Contatti />} />
              <Route path="/prenota" element={<Booking />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/ordine-confermato" element={<OrderConfirmation />} />
              <Route path="/area-personale" element={<AreaPersonale />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/termini-condizioni" element={<TerminiCondizioni />} />
              <Route path="/recesso" element={<Recesso />} />

              {/* Admin Routes */}
              <Route path="/admin" element={<AdminDashboard />}>
                <Route index element={<AdminHome />} />
                <Route path="orders" element={<AdminOrders />} />
                <Route path="bookings" element={<AdminBookings />} />
                <Route path="booking-audit" element={<AdminBookingAudit />} />
                <Route path="reviews" element={<AdminReviews />} />
                <Route path="settings" element={<AdminSettings />} />
                <Route path="hours" element={<AdminHours />} />
                <Route path="pages" element={<AdminPages />} />
                <Route path="services" element={<AdminServices />} />
                <Route path="events" element={<AdminEvents />} />
                <Route path="courses" element={<AdminCourses />} />
                

                <Route path="gift-cards" element={<AdminGiftCards />} />
                <Route path="emails" element={<AdminEmails />} />
                <Route path="payments" element={<AdminPayments />} />
                <Route path="recessi" element={<AdminRecessi />} />
              </Route>

              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
